import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { formatDistanceToNow } from 'date-fns';
import { SlidersHorizontal } from 'lucide-react';

interface SortableDealCardProps {
  deal: any;
  onClick: (deal: any) => void;
}

export default function SortableDealCard({ deal, onClick }: SortableDealCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: deal.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = (e: React.MouseEvent) => {
    // Only trigger click if we're not dragging
    if (!isDragging) {
      onClick(deal);
    }
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing relative"
      onClick={handleClick}
    >
      {/* Drag Indicator */}
      <div className="absolute top-2 right-2 opacity-30">
        <SlidersHorizontal className="w-3 h-3" />
      </div>
      
      <CardHeader className="p-3 pb-0">
        <CardTitle className="text-sm">{deal.title}</CardTitle>
        <CardDescription className="text-xs">
          {deal.customer?.firstName} {deal.customer?.lastName}
          {deal.customer?.companyName && ` • ${deal.customer.companyName}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-3 pt-2">
        <div className="text-sm font-medium">
          ${deal.value.toLocaleString()}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          Expected close: {new Date(deal.expectedCloseDate).toLocaleDateString()}
        </div>
      </CardContent>
      <CardFooter className="p-3 pt-0 flex justify-between items-center">
        <div className="text-xs text-muted-foreground">
          {deal.dateUpdated 
            ? `Updated ${formatDistanceToNow(new Date(deal.dateUpdated), { addSuffix: true })}`
            : `Created ${formatDistanceToNow(new Date(deal.dateCreated), { addSuffix: true })}`
          }
        </div>
        <Avatar className="w-6 h-6">
          <AvatarImage src={deal.owner?.profileImg} />
          <AvatarFallback>{deal.owner?.firstName?.[0]}{deal.owner?.lastName?.[0]}</AvatarFallback>
        </Avatar>
      </CardFooter>
    </Card>
  );
} 